import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser, setAuthToken, setAuthType } from '../../store/slices/userSlice';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CButton, CInput, CText, CHeader } from '../../components/common';
import {
  requestOTP,
  registerWithPhone,
  handlePostLoginData,
  handleEmailLogin,
  handleEmailSignUp,
  fetchUserData,
  fetchClientConfig,
} from '../../api/auth';
import {
  setAuthToken as saveAuthToken,
  setAuthType as saveAuthType,
  setUserData as saveUserData,
} from '../../utils/localStorage';
import { GOOGLE_AUTH_TYPE, EMAIL_AUTH_TYPE } from '../../utils/constants';
import { emailValidation } from '../../utils/validation';

type AuthMode = 'signin' | 'signup';

const Auth: React.FC<{ googleClientId: string }> = ({ googleClientId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);

  // Default to signup mode if navigated to /signup
  const initialMode: AuthMode =
    (location.state as any)?.mode === 'signup' || location.pathname === '/signup'
      ? 'signup'
      : 'signin';

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [activeTab, setActiveTab] = useState<'phone' | 'email'>('phone');
  const [loading, setLoading] = useState(false);

  // Shared fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Signup-only fields
  const [fullName, setFullName] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Clear errors when switching modes or tabs
  useEffect(() => {
    setPhoneNumberError('');
    setEmailError('');
    setPasswordError('');
    setFullNameError('');
    setConfirmPasswordError('');
  }, [mode, activeTab]);

  // --- Google OAuth ---
  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    try {
      const idToken = credentialResponse.credential;
      const response = await handlePostLoginData(idToken, 'GOOGLE');

      saveAuthToken(response.access || '');
      saveAuthType(GOOGLE_AUTH_TYPE);
      dispatch(setAuthToken(response.access || ''));
      dispatch(setAuthType(GOOGLE_AUTH_TYPE));

      const userData = await fetchUserData(response.access || '');
      dispatch(setUser(userData));
      saveUserData(userData);

      if (!userData.user_exists) {
        navigate(routes.userDetails);
      } else {
        navigate(routes.home);
      }
    } catch (error: any) {
      console.error('Error with Google login:', error);
      alert(error.response?.data?.error || 'Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    alert('Google login failed. Please try again.');
  };

  // --- Phone: Sign In (Get OTP) ---
  const handlePhoneSignIn = async () => {
    let isValid = true;
    if (!phoneNumber) {
      setPhoneNumberError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (phoneNumber.length < 10) {
      setPhoneNumberError('Please enter a valid phone number');
      isValid = false;
    } else {
      setPhoneNumberError('');
    }
    if (!isValid) return;

    setLoading(true);
    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber}`.trim();
      await requestOTP(fullPhoneNumber);
      navigate(routes.otpVerification || '/otp-verification', {
        state: { phoneNumber: fullPhoneNumber },
      });
    } catch (error: any) {
      console.error('Error requesting OTP:', error);
      setPhoneNumberError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to send OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // --- Phone: Sign Up (Register + OTP) ---
  const handlePhoneSignUp = async () => {
    let isValid = true;
    if (!fullName.trim()) {
      setFullNameError(strings.thisFieldIsMandatory);
      isValid = false;
    } else {
      setFullNameError('');
    }
    if (!phoneNumber) {
      setPhoneNumberError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (phoneNumber.length < 10) {
      setPhoneNumberError('Please enter a valid phone number');
      isValid = false;
    } else {
      setPhoneNumberError('');
    }
    if (!isValid) return;

    setLoading(true);
    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber}`.trim();
      await registerWithPhone(fullPhoneNumber);
      navigate(routes.otpVerification, {
        state: { phoneNumber: fullPhoneNumber, fullName: fullName.trim() },
      });
    } catch (error: any) {
      console.error('Error during signup:', error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        '';
      if (error.response?.status === 400 && errorMsg.toLowerCase().includes('exists')) {
        setPhoneNumberError('This phone number is already registered. Please sign in instead.');
      } else {
        setPhoneNumberError(errorMsg || 'Failed to register. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Email: Sign In ---
  const handleEmailSignIn = async () => {
    let isValid = true;
    if (!email) {
      setEmailError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (!emailValidation(email)) {
      setEmailError(strings.enterValidEmail);
      isValid = false;
    } else {
      setEmailError('');
    }
    if (!password) {
      setPasswordError(strings.thisFieldIsMandatory);
      isValid = false;
    } else {
      setPasswordError('');
    }
    if (!isValid) return;

    setLoading(true);
    try {
      const response = await handleEmailLogin(email, password);

      saveAuthToken(response.access || '');
      saveAuthType(EMAIL_AUTH_TYPE);
      dispatch(setAuthToken(response.access || ''));
      dispatch(setAuthType(EMAIL_AUTH_TYPE));

      const userData = await fetchUserData(response.access || '');
      dispatch(setUser(userData));
      saveUserData(userData);

      if (!userData.user_exists) {
        navigate(routes.userDetails);
      } else {
        navigate(routes.home);
      }
    } catch (error: any) {
      console.error('Error with email login:', error);
      const errorMsg = error.response?.data?.error || 'Login failed. Please try again.';
      setPasswordError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // --- Email: Sign Up ---
  const handleEmailSignUpSubmit = async () => {
    let isValid = true;
    if (!fullName.trim()) {
      setFullNameError(strings.thisFieldIsMandatory);
      isValid = false;
    } else {
      setFullNameError('');
    }
    if (!email) {
      setEmailError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (!emailValidation(email)) {
      setEmailError(strings.enterValidEmail);
      isValid = false;
    } else {
      setEmailError('');
    }
    if (!password) {
      setPasswordError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isValid = false;
    } else {
      setPasswordError('');
    }
    if (!confirmPassword) {
      setConfirmPasswordError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError(strings.confirmPassValidString);
      isValid = false;
    } else {
      setConfirmPasswordError('');
    }
    if (!isValid) return;

    setLoading(true);
    try {
      const response = await handleEmailSignUp(email, password, fullName.trim());

      saveAuthToken(response.access || '');
      saveAuthType(EMAIL_AUTH_TYPE);
      dispatch(setAuthToken(response.access || ''));
      dispatch(setAuthType(EMAIL_AUTH_TYPE));

      const userData = await fetchUserData(response.access || '');
      dispatch(setUser(userData));
      saveUserData(userData);

      navigate(routes.userDetails);
    } catch (error: any) {
      console.error('Error during email signup:', error);
      const errorMsg = error.response?.data?.error || 'Signup failed. Please try again.';
      if (errorMsg.toLowerCase().includes('already exists')) {
        setEmailError('This email is already registered. Please sign in instead.');
      } else {
        setEmailError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const googleAuthEnabled = !!googleClientId;
  const isSignUp = mode === 'signup';

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.backgroundColor }}>
      <CHeader title={isSignUp ? strings.signUp : strings.signIn} />

      <div className="max-w-md md:max-w-lg lg:max-w-xl mx-auto px-5 py-4">
        <h2
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: colors.textColor }}
        >
          {isSignUp ? strings.createYourAccount : strings.welcomeBack}
        </h2>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
          {isSignUp ? strings.accountText : strings.signInToYourAccount}
        </CText>

        {/* Tab toggle: Phone | Email */}
        <div
          className="flex mb-4 rounded-xl overflow-hidden border"
          style={{ borderColor: colors.borderColor }}
        >
          <button
            className="flex-1 py-2.5 text-sm font-semibold transition-colors"
            style={{
              backgroundColor: activeTab === 'phone' ? colors.primary : 'transparent',
              color: activeTab === 'phone' ? '#fff' : colors.textColor,
            }}
            onClick={() => setActiveTab('phone')}
          >
            {strings.phoneNumber}
          </button>
          <button
            className="flex-1 py-2.5 text-sm font-semibold transition-colors"
            style={{
              backgroundColor: activeTab === 'email' ? colors.primary : 'transparent',
              color: activeTab === 'email' ? '#fff' : colors.textColor,
            }}
            onClick={() => setActiveTab('email')}
          >
            {strings.email}
          </button>
        </div>

        {/* Phone tab */}
        {activeTab === 'phone' ? (
          <>
            {isSignUp && (
              <CInput
                label={strings.fullName}
                value={fullName}
                onChange={setFullName}
                placeholder="Enter your full name"
                errorText={fullNameError}
                required
              />
            )}

            <div className="mb-3">
              <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '4px' }}>
                Country Code
              </CText>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full p-3 rounded-xl border text-base font-sans"
                style={{
                  borderColor: colors.borderColor,
                  backgroundColor: colors.inputBackground,
                  color: colors.textColor,
                }}
              >
                <option value="+91">India (+91)</option>
                <option value="+1">USA (+1)</option>
                <option value="+44">UK (+44)</option>
                <option value="+971">UAE (+971)</option>
              </select>
            </div>

            <CInput
              label={strings.phoneNumber}
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="Enter your phone number"
              type="tel"
              maxLength={10}
              errorText={phoneNumberError}
              required
            />

            <CButton
              title={
                loading
                  ? 'Sending OTP...'
                  : isSignUp
                    ? strings.signUp
                    : strings.getOTP
              }
              onPress={isSignUp ? handlePhoneSignUp : handlePhoneSignIn}
              disabled={loading}
            />
          </>
        ) : (
          /* Email tab */
          <>
            {isSignUp && (
              <CInput
                label={strings.fullName}
                value={fullName}
                onChange={setFullName}
                placeholder="Enter your full name"
                errorText={fullNameError}
                required
              />
            )}

            <CInput
              label={strings.email}
              value={email}
              onChange={setEmail}
              placeholder="Enter your email"
              type="email"
              errorText={emailError}
              required
            />

            <CInput
              label={strings.password}
              value={password}
              onChange={setPassword}
              placeholder="Enter your password"
              type="password"
              errorText={passwordError}
              required
            />

            {isSignUp && (
              <CInput
                label={strings.confirmPassword}
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Confirm your password"
                type="password"
                errorText={confirmPasswordError}
                required
              />
            )}

            <CButton
              title={
                loading
                  ? isSignUp
                    ? 'Creating Account...'
                    : 'Signing In...'
                  : isSignUp
                    ? strings.signUp
                    : strings.signIn
              }
              onPress={isSignUp ? handleEmailSignUpSubmit : handleEmailSignIn}
              disabled={loading}
            />
          </>
        )}

        {/* Google OAuth */}
        {googleAuthEnabled && (
          <>
            <div className="flex items-center my-4 gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: colors.grayScale400 }} />
              <CText type="r12" color={colors.grayScale500}>OR</CText>
              <div className="flex-1 h-px" style={{ backgroundColor: colors.grayScale400 }} />
            </div>

            <div className="flex justify-center mb-4">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            </div>
          </>
        )}

        {/* Agreement text (shown in both modes) */}
        <div className="mt-2 mb-2">
          <CText type="r12" color={colors.grayScale500}>
            {strings.agreementText}{' '}
            <span className="cursor-pointer" style={{ color: colors.primary }}>
              {strings.userAgreement}
            </span>
            {' '}{strings.and}{' '}
            <span
              className="cursor-pointer"
              style={{ color: colors.primary }}
              onClick={() => navigate(routes.privacyPolicies)}
            >
              {strings.privacyPolicy}
            </span>
          </CText>
        </div>

        {/* Mode toggle */}
        <div className="text-center mt-4">
          <CText type="r14" color={colors.grayScale500}>
            {isSignUp ? strings.alreadyHaveAccount : strings.doHaveAnAccount}
            <span
              className="cursor-pointer ml-1"
              style={{ color: colors.primary }}
              onClick={() => setMode(isSignUp ? 'signin' : 'signup')}
            >
              {isSignUp ? strings.signIn : strings.signUp}
            </span>
          </CText>
        </div>
      </div>
    </div>
  );
};

const AuthWithProvider: React.FC = () => {
  const [googleClientId, setGoogleClientId] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    fetchClientConfig()
      .then((cfg) => {
        setGoogleClientId(cfg.google_client_id || '');
      })
      .catch((err) => {
        console.error('Failed to fetch client config:', err);
      })
      .finally(() => setConfigLoaded(true));
  }, []);

  if (!configLoaded) {
    return null;
  }

  if (!googleClientId) {
    return <Auth googleClientId="" />;
  }
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Auth googleClientId={googleClientId} />
    </GoogleOAuthProvider>
  );
};

export default AuthWithProvider;
